import { apiMerchantPublic } from "@/api";
import { apiMerchantURL } from "@/consts";

import { readCartId } from "./cart";
import { ensureStorefrontClaim } from "./claim";
import { generateEventId, getOrCreateSessionId, isTrackingOptedOut } from "./session";
import type { StorefrontEvent, StorefrontEventInput } from "./types";

const FLUSH_INTERVAL_MS = 5000;
const HEARTBEAT_INTERVAL_MS = 50_000;
const MAX_BATCH_SIZE = 20;
const MAX_EVENTS_PER_REQUEST = 50;
const EVENTS_TIMEOUT_MS = 1500;

/** Which event types are recorded at most once per tab session, and the key that identifies "once". */
function dedupeKey(event: StorefrontEventInput): string | null {
  switch (event.type) {
    case "menu_view":
      return "menu_view";
    case "item_view":
      return `item_view:${event.itemId}`;
    case "checkout_step":
      return `checkout_step:${event.checkoutStep}`;
    default:
      return null;
  }
}

/**
 * WHERE THE STOREFRONT RECORDS ANALYTICS EVENTS
 *
 * `POST /public/merchants/:id/storefront/events`, fire-and-forget: the
 * gateway answers `202` unconditionally (fail-silent), so nothing here ever
 * blocks the UX or retries on failure. Same mechanics as
 * `SuggestionEventsSink`: in-memory queue, flush every ~5s or at 20 events
 * (capped at 50/request), plus an eager `fetch(keepalive)` flush on
 * `visibilitychange`/`pagehide`. Does nothing at all when
 * `navigator.globalPrivacyControl`/`doNotTrack` opt the visitor out.
 */
export class StorefrontAnalyticsSink {
  private readonly merchantId: string;
  private readonly sessionId: string;
  private readonly optedOut: boolean;
  private queue: StorefrontEvent[] = [];
  private readonly seenOnceKeys = new Set<string>();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private heartbeatId: ReturnType<typeof setInterval> | null = null;
  private engagementStartedAt = 0;

  constructor(merchantId: string) {
    this.merchantId = merchantId;
    this.sessionId = getOrCreateSessionId(merchantId);
    this.optedOut = isTrackingOptedOut();
  }

  /** Queues an event, deduping `menu_view`/`item_view`/`checkout_step` once per session. No-op entirely when opted out. */
  enqueue(event: StorefrontEventInput): void {
    if (this.optedOut) return;

    const key = dedupeKey(event);
    if (key !== null) {
      if (this.seenOnceKeys.has(key)) return;
      this.seenOnceKeys.add(key);
    }

    let occurredAt: string;
    try {
      occurredAt = new Date().toISOString();
    } catch {
      occurredAt = "";
    }

    this.queue.push({
      ...event,
      eventId: generateEventId(),
      sessionId: this.sessionId,
      occurredAt,
    });

    if (this.queue.length >= MAX_BATCH_SIZE) void this.flush();
  }

  /** Starts the periodic flush + unload listeners. Idempotent. No-op when opted out. */
  start(): () => void {
    if (this.optedOut) return () => {};

    this.engagementStartedAt = Date.now();
    if (this.intervalId === null) {
      this.intervalId = setInterval(() => void this.flush(), FLUSH_INTERVAL_MS);
    }
    if (this.heartbeatId === null) {
      this.heartbeatId = setInterval(() => this.beat(false), HEARTBEAT_INTERVAL_MS);
    }

    const onHide = () => {
      if (document.visibilityState === "hidden") {
        this.beat(true);
        this.flushOnUnload();
      } else {
        this.engagementStartedAt = Date.now();
      }
    };
    const onPageHide = () => this.flushOnUnload();

    try {
      if (typeof document !== "undefined") document.addEventListener("visibilitychange", onHide);
      if (typeof window !== "undefined") window.addEventListener("pagehide", onPageHide);
    } catch {
      // SSR/prerender/test environment without a DOM — nothing to attach to
    }

    return () => {
      if (this.intervalId !== null) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
      if (this.heartbeatId !== null) {
        clearInterval(this.heartbeatId);
        this.heartbeatId = null;
      }
      try {
        if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onHide);
        if (typeof window !== "undefined") window.removeEventListener("pagehide", onPageHide);
      } catch {
        // same guard as above
      }
    };
  }

  /** Queues foreground time while the tab is visible. A hidden tab does not keep the session alive. */
  private beat(includeHidden: boolean): void {
    try {
      if (!includeHidden && document.visibilityState === "hidden") return;
    } catch {
      return;
    }
    const now = Date.now();
    const engagementMs = Math.max(0, Math.min(120_000, now - this.engagementStartedAt));
    this.engagementStartedAt = now;
    if (engagementMs <= 0) return;
    const cartId = readCartId(this.merchantId) ?? undefined;
    this.enqueue({ type: "heartbeat", engagementMs, cartId });
  }

  /** Sends up to `MAX_EVENTS_PER_REQUEST` queued events, settling the customer claim first. Never throws; drops the batch on failure. An empty queue sends nothing, not even the claim. */
  async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    if (!this.optedOut) await ensureStorefrontClaim();

    const batch = this.queue.slice(0, MAX_EVENTS_PER_REQUEST);
    this.queue = this.queue.slice(MAX_EVENTS_PER_REQUEST);

    try {
      await apiMerchantPublic.post(
        `/public/merchants/${encodeURIComponent(this.merchantId)}/storefront/events`,
        { events: batch },
        { timeout: EVENTS_TIMEOUT_MS },
      );
    } catch {
      return;
    }
  }

  /**
   * Best-effort send on tab hide/unload via `fetch(keepalive)` — axios does
   * not support `keepalive`, and an unload is not the moment to await a
   * promise anyway. Never rejects: a frozen/offline tab just drops the batch.
   */
  private flushOnUnload(): void {
    if (this.queue.length === 0) return;
    const batch = this.queue.slice(0, MAX_EVENTS_PER_REQUEST);
    this.queue = this.queue.slice(MAX_EVENTS_PER_REQUEST);

    try {
      const url = `${apiMerchantURL.replace(/\/merchants\/?$/, "")}/public/merchants/${encodeURIComponent(
        this.merchantId,
      )}/storefront/events`;
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      }).catch(() => undefined);
    } catch {
      return;
    }
  }

  /** Tests only. */
  reset(): void {
    this.queue = [];
    this.seenOnceKeys.clear();
  }
}

const sinksByMerchant = new Map<string, StorefrontAnalyticsSink>();

/** One sink per merchant per session — the storefront never juggles two stores at once, but tests do. */
export function getStorefrontAnalyticsSink(merchantId: string): StorefrontAnalyticsSink {
  let sink = sinksByMerchant.get(merchantId);
  if (!sink) {
    sink = new StorefrontAnalyticsSink(merchantId);
    sinksByMerchant.set(merchantId, sink);
  }
  return sink;
}

/** Tests only: forget every sink created so far. */
export function resetStorefrontAnalyticsSinks(): void {
  sinksByMerchant.clear();
}
